import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import headerImage from "./assets/ai-engeeningr/header.png";
import workspace from "./assets/ai-engeeningr/workspace-overview.png";
import architecture from "./assets/ai-engeeningr/hybrid-architecture.png";
import rag from "./assets/ai-engeeningr/rag-pipeline.png";
import creation from "./assets/ai-engeeningr/multimodal-creation.png";
import guard from "./assets/ai-engeeningr/content-guard.png";
import "./DualAIPages.css";

const pageLinks = [
  ["/dual-ai", "Overview"],
  ["/use-cases/ai-proposal", "Proposal Engine"],
  ["/dual-ai/generation", "Generation Engine"],
  ["/dual-ai/connexion", "Grounding & RAG"],
  ["/dual-ai?view=connective", "Connective AI"],
  ["/dual-ai/workflow", "Workflow & Guardrails"],
];

const diagrams = {
  workspace: [workspace, "AI Workspace — technical overview"],
  architecture: [architecture, "Hybrid AI Architecture"],
  rag: [rag, "RAG Recommendation Pipeline"],
  creation: [creation, "Multimodal Creation"],
  guard: [guard, "Content Guard and Trust"],
};

export function AIDiagram({ kind }) {
  const [src, alt] = diagrams[kind];
  return (
    <figure className="dai-diagram">
      <a href={src} target="_blank" rel="noopener noreferrer" aria-label={`${alt} — open full image`}>
        <img src={src} alt={alt} loading="lazy" width="1672" height="941" />
      </a>
      <figcaption>
        <span>{alt}</span>
        <span aria-hidden="true">↗</span>
      </figcaption>
    </figure>
  );
}

export function AIVideo({ src, label }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const video = ref.current;
    if (!video) return undefined;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => {
      if (reduced.matches) video.pause();
    };

    const observer = typeof IntersectionObserver !== "undefined"
      ? new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting && !reduced.matches) video.play().catch(() => {});
            else video.pause();
          },
          { threshold: 0.25 }
        )
      : null;

    observer?.observe(video);
    reduced.addEventListener?.("change", update);

    return () => {
      observer?.disconnect();
      reduced.removeEventListener?.("change", update);
      video.pause();
    };
  }, []);

  return (
    <figure className="dai-video">
      <div className="dai-mediaBar">
        <span className={playing ? "dai-liveDot is-playing" : "dai-liveDot"} />
        <span>{label}</span>
        <span aria-hidden="true">↗</span>
      </div>
      <video
        ref={ref}
        muted
        loop
        playsInline
        controls
        preload="metadata"
        aria-label={label}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      >
        <source src={src} type="video/mp4" />
        Your browser does not support this video.
      </video>
    </figure>
  );
}

const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.akremanaya.weavevomobile&hl=fr";

function MobileDownloadSection() {
  return (
    <section className="dai-mobileCta" aria-labelledby="dai-mobile-title">
      <h2 id="dai-mobile-title">Activate Your Organization Workspace on Mobile</h2>
      <p>
        Access your Living Weave campaigns within a sandboxed B2B workspace. Download the app to monitor
        ecosystem contributions and trigger The Loom&apos;s orchestration pipeline on iOS and Android.
      </p>
      <div className="dai-storeActions" aria-label="Download Weavevo">
        <a
          className="dai-storeButton dai-storeButtonPrimary"
          href={GOOGLE_PLAY_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open Weavevo on the Play Store"
        >
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
            <path fill="currentColor" d="M3 2.7v18.6L13.1 12 3 2.7Zm1.5-.5 11.9 6.9-2.4 2.2L4.5 2.2Zm12.9 7.5 3 1.7c.5.3.5.9 0 1.2l-3 1.7-2.5-2.3 2.5-2.3Zm-1 5.2L4.5 21.8l9.5-9.1 2.4 2.2Z" />
          </svg>
          <span>Play Store</span>
        </a>
        <button className="dai-storeButton dai-storeButtonSecondary" type="button" disabled aria-label="Weavevo App Store button" title="App Store">
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
            <path fill="currentColor" d="M16.7 1.5c.1 1.3-.4 2.5-1.2 3.4-.8.9-1.9 1.5-3.1 1.4-.2-1.2.4-2.5 1.2-3.4.8-.9 2-1.4 3.1-1.4ZM20.5 17.3c-.5 1.2-.8 1.8-1.5 2.8-1 1.5-2.4 3.3-4.1 3.3-1.5 0-1.9-1-3.9-1-2 0-2.5 1-4 1-1.6 0-2.9-1.6-3.9-3.1C.3 16.1-.1 10.4 1.6 7.6 2.8 5.6 4.8 4.4 6.7 4.4c1.7 0 2.8 1 4.2 1 1.3 0 2.3-1 4.2-1 1.6 0 3.3.9 4.5 2.3-4 2.2-3.4 7.9.9 10.6Z" transform="translate(2 1) scale(.85)" />
          </svg>
          <span>App Store</span>
        </button>
      </div>
    </section>
  );
}

export function AIPageShell({
  pageTitle,
  currentPath,
  eyebrow,
  heroTitle,
  heroText,
  finalEyebrow,
  finalTitle,
  finalText,
  hideHeroActions = false,
  children,
}) {
  useEffect(() => {
    document.title = `${pageTitle} | Weavevo`;
    window.scrollTo(0, 0);
  }, [pageTitle, currentPath]);

  const index = pageLinks.findIndex(([path]) => path === currentPath);
  const next = pageLinks[(index + 1 + pageLinks.length) % pageLinks.length];

  return (
    <main className="dai-page">
      <a className="dai-skip" href="#dai-content">Skip to content</a>
      <section className="dai-hero">
        <img className="dai-heroImage" src={headerImage} alt="" fetchPriority="high" />
        <div className="dai-heroScrim" />
        <div className="dai-heroGrid" aria-hidden="true" />
        <div className="dai-heroContent">
          <span className="dai-eyebrow"><i aria-hidden="true" />{eyebrow}</span>
          <h1>{heroTitle}</h1>
          <p>{heroText}</p>
          {!hideHeroActions && (
            <div className="dai-actions">
              <a className="dai-button dai-buttonPrimary" href="#dai-content">
                {pageTitle}
                <span aria-hidden="true">↓</span>
              </a>
              <Link className="dai-button dai-buttonGlass" to={next[0]}>
                {next[1]}
                <span aria-hidden="true">↗</span>
              </Link>
            </div>
          )}
        </div>
        <div className="dai-heroSignature" aria-hidden="true">
          <span>WEAVEVO AI</span>
          <span>Proposal · Generate · Connect · Guard</span>
        </div>
      </section>


      <div id="dai-content" className="dai-content">{children}</div>

      <section className="dai-final">
        <div className="dai-finalOrbit" aria-hidden="true" />
        <span className="dai-eyebrow">{finalEyebrow}</span>
        <h2>{finalTitle}</h2>
        <p>{finalText}</p>
        <div className="dai-actions">
          <Link className="dai-button dai-buttonPrimary" to={next[0]}>
            {next[1]}
            <span aria-hidden="true">→</span>
          </Link>
        </div>
      </section>
      <MobileDownloadSection />
    </main>
  );
}
